import axios, {  AxiosRequestConfig, Method } from "axios";
import { NewPage, Page, Question, User } from "./types";


/** API Class.
 */

export const API_TOKEN_KEY = "SwissConfToken";

class SwissConfApi {
  // the token for interactive with the API will be stored here.
  static token: string;

  static getApiToken() {
    return localStorage.getItem(API_TOKEN_KEY) || SwissConfApi.token;
  }

  static async request(endpoint:string, data = {}, method: Method = "get", errorAsIs: boolean = false) {
    console.debug("API Call:", endpoint, data, method);

    const url = `/api${endpoint}`;
    const headers = { Authorization: `Bearer ${SwissConfApi.getApiToken()}` };
    const params = (method === "get")
        ? data
        : {};

    try {
      return (await axios({ url, method, data, params, headers })).data;
    } catch (err:any) {
      console.error("API Error:", err.response);
      if(errorAsIs) {
        throw err;
      } else {
        let message = err.response.data.error.message;
        throw Array.isArray(message) ? message : [message];
      }
    }
  }

  static async request2(method: Method, endpoint: string, params: Partial<AxiosRequestConfig<any>> = {}) {
    console.debug("API Call:", endpoint, method, params);

    const url = `/api${endpoint}`;
    const paramsAll = {
      ...params,
      url,
      method,
      headers: { Authorization: `Bearer ${SwissConfApi.getApiToken()}`, ...(params.headers || {}) },
    }

    try {
      return (await axios(paramsAll)).data;
    } catch (err:any) {
      console.error("API Error:", err.response);
      let message = err.response.data.error.message;
      throw Array.isArray(message) ? message : [message];
    }
  }

  // Individual API routes:

 //***1. Users***//

  // Get all users
  static async getAllusers(){
    let res = await this.request("/users");
    return res.users;
  }

  // Get user by username
  static async getUser(username:string){
    let res = await this.request(`/users/${username}`);
    return res.user;
  }


  //Register user
  static async registerUser({username, email, password, first_name, last_name} : {username:string, email:string, password: string, first_name:string, last_name:string}){
    let res = await this.request(`/auth/register`, {username, email, password, first_name, last_name}, 'post');
    return res.token;
  }

  //Add user function for logged in super admin
  static async addUser({username, email, password, first_name, last_name} : {username:string, email:string, password: string, first_name:string, last_name:string}){
    let res = await this.request(`/users`, {username, email, password, first_name, last_name}, 'post');
    return res.token;
  }

  //Login user
  static async loginUser({username, password} : {username:string, password:string}){
    let res = await this.request(`/auth/login`, {username, password}, 'post');
    return res.token;
  }

  static async loginAs(username: string) {
    const result = await this.request("/auth/login-as", { username }, "post");
    return result.token;
  }

  //Update user
  static async updateUser(username:string, changes: Partial<Omit<User, "username">>){
    let res = await this.request(`/users/${username}`, changes, 'patch');
    return res.user;
  }

  //Delete user
  static async deleteUser(username:string){
    let res = await this.request(`/users/${username}`, undefined, 'delete');
    return res;
  }



  //***2. Events***//

  // Get all events
  static async getAllEvents(){
    let res = await this.request("/events");
    return res.events;
  }

  // Get event by event_id
  static async getEventbyId(event_id:number){
    let res = await this.request(`/events/${event_id}`);
    return res.event;
  }

  // Get event by domain name
  static async getEventbyName(name:string){
    let res = await this.request(`/events/${name}`);
    return res.event;
  }

  //Create a new event
  static async createEvent({name, title, description }: { name: string, title:string, description?:string }){
    let res = await this.request(`/events`, { name, title, description: description || "" }, 'post');
    return res.event;
  }


  //Update event by id
  static async updateEvent (event_id:number, changes : Partial<Page>){
    let res = await this.request(`/events/${event_id}`, changes, 'patch');
    return res.event;
  }

  //Delete event by id
  static async deleteEvent(event_id:number){
    let res = await this.request(`/events/${event_id}`, 'delete');
    return res.event;
  }


  //***3. Pages***//

  // Get all pages for an event by event_id
  static async getAllPages(event_id:number){
    let res = await this.request(`/pages/${event_id}`);
    return res.pages;
  }

  // Get a singly page for an event by event_id
  static async getPage(event_id:number, id:number){
    let res = await this.request(`/pages/${event_id}/${id}`);
    return res.page;
  }


  //Create a new page for an event by event_id
  static async createPage(event_id:number, { slug, title, content } : NewPage){
    let res = await this.request(
      `/pages/${event_id}`,
      { slug, title, content: content || [{ layout: { columns: "1fr" }, components: [] }] },
      'post');
    return res.page;
  }


  //Update title and slug for a page by event_id and id
  static async updatePage (event_id:number, id:number, changes : Partial<Page>) {
    let res = await this.request(`/pages/${event_id}/${id}`, changes, 'patch');
    return res.page;
  }

  //Update the content of an page by event_id and slug
  static async updatePageContent (event_id:number, slug:string, content:object){
    let res = await this.request(`/pages/${event_id}/${slug}/edit`, content, 'patch');
    return res.event;
  }

  //Delete page by event_id and slug
  static async deletePage(event_id:number, id: number){
    let res = await this.request(`/pages/${event_id}/${id}`, undefined, 'delete');
    return res;
  }


   //***4. Images***//

  // Get all images for an event by event_id
  static async getAllImages(event_id:number){
    let res = await this.request(`/images/${event_id}`);
    return res.image;
  }

  // Get a single image for an event by event_id and id
  static async getImage(event_id:number, id:number){
    let res = await this.request(`/images/${event_id}/${id}`);
    return res.image;
  }


  //Create a new image for an event by event_id
  static async createImage(event_id:number, path:string){
    let res = await this.request(`/images/${event_id}`, path, 'post');
    return res.image;
  }


  //Update the path of an image by event_id and id
  static async updateImage (event_id:number, id:number, path:string){
    let res = await this.request(`/images/${event_id}/${id}`,path, 'patch');
    return res.image;
  }

  //Delete image by event_id and slug
  static async deleteImage(event_id:number, id:number){
    let res = await this.request(`/images/${event_id}/${id})`, 'delete');
    return res.image;
  }

  static async getStyles(event_id: number) {
    const res = await this.request(`/events/${event_id}/style`, "get");
    return res.style;
  }

  static async createStyles(event_id: number, styles: any) {
    const res = await this.request(`/events/${event_id}/style`, styles, "post");
    return res.style;
  }

  static async updateStyles(event_id: number, changes: any) {
    const res = await this.request(`/events/${event_id}/style`, changes, "patch");
    return res.style;
  }


  static async getAllQuestions(event_id: number, view: string): Promise<Question[]> {
    return await this.request(`/events/${event_id}/questions?view=${view}`, undefined, "get");
  }

  static async updateQuestion(event_id: number, question_id: number, changes: Pick<Question, "is_processed">): Promise<Question> {
    return await this.request(`/events/${event_id}/questions/${question_id}`, changes, "put");
  }

}



export default SwissConfApi